/* eslint-disable no-mixed-operators */
// Login.jsx
import React, { useState, useEffect } from 'react';
import { T } from "./Translate";
import { useNavigate } from 'react-router-dom';
import { signInUser, signInWithGoogle, db, auth, functions } from '../firebase';
import { getAuth, getMultiFactorResolver } from 'firebase/auth';
import { startAuthentication, browserSupportsWebAuthn, browserSupportsWebAuthnAutofill } from '@simplewebauthn/browser';
import { isNativePasskeyBridgeAvailable, nativeStartPasskeyLogin } from '../utils/nativePasskey';
import { deviceHasAnyPasskeyRemembered, rememberPasskeyForEmail } from '../utils/passkeyDevice';
import { useGeolocation } from '../hooks/useGeolocation';
import { isLoggedIn } from '../session';
import LoginForm from './LoginForm';
import WelcomeSlides from './WelcomeSlides';
import MfaChallenge from './MfaChallenge';
import AuthLayout from './AuthLayout';
import { getIpLocation } from '../services/ipLocation';
// Guardar acceso en Firestore solo con coordenadas
async function saveAccessLog(user, latitude, longitude) {
  const now = new Date();
  const location = await getIpLocation();
  const country = location?.countryName || 'Desconocido';
  const access = {
    timestamp: now.getTime(),
    date: now.toLocaleDateString(),
    time: now.toLocaleTimeString(),
    iso: now.toISOString(),
    latitude,
    longitude,
    country
  };
  try {
    await db.collection('users').doc(user.uid).collection('accessLogs').add(access);
  } catch {}
}

// Login con Google en curso. Vive fuera del componente a propósito: apenas
// Firebase confirma la sesión, App.js reemplaza toda la pantalla por <Loading />
// mientras chequea el 2FA, y eso desmonta este componente. Al volver se
// montaba de cero (formulario normal, botón habilitado) mientras la consulta
// de la cuenta seguía corriendo: en la app Android, con la latencia del
// celular, parecía que no había pasado nada y recién mucho después saltaba al
// inicio o al registro. Con esto el remontado sigue mostrando el spinner.
let pendingGoogleSignIn = null;

const GOOGLE_CANCEL_CODES = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPresentation, setShowPresentation] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  // Con una llave de acceso recordada el login arranca en modo passkey, pero
  // en el mismo dispositivo puede haber otra cuenta sin llave: "Usar otra
  // cuenta" vuelve a mostrar email y contraseña.
  const [usePasswordInstead, setUsePasswordInstead] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(() => !!pendingGoogleSignIn);

  const handleGoogleError = (err) => {
    if (err.code === 'auth/multi-factor-auth-required') {
      setMfaResolver(getMultiFactorResolver(getAuth(), err));
    } else if (!GOOGLE_CANCEL_CODES.includes(err.code)) {
      setError(err.message || 'No se pudo iniciar sesión con Google.');
    }
  };

  // Remontado en medio de un login con Google (ver pendingGoogleSignIn): la
  // navegación la sigue haciendo el handler original; acá solo se muestra el
  // estado de carga y, si falla, el error.
  useEffect(() => {
    const pending = pendingGoogleSignIn;
    if (!pending) return undefined;
    let active = true;
    pending
      .catch((err) => active && handleGoogleError(err))
      .finally(() => active && setGoogleLoading(false));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasSeenSlides = localStorage.getItem('hasSeenWelcomeSlides');
    if (!hasSeenSlides) {
      setShowPresentation(true);
    }
  }, []);

  const handleFinishSlides = () => {
    localStorage.setItem('hasSeenWelcomeSlides', 'true');
    setShowPresentation(false);
  };

  const handleChange = (prop) => (event) => {
    setFormData({ ...formData, [prop]: event.target.value });
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError(<T>Por favor ingrese su usuario y contraseña.</T>);
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(<T>Por favor ingrese un email válido.</T>);
      return false;
    }
    return true;
  };



  const { position, loading: geoLoading } = useGeolocation();

  // Ya logueado con contraseña sabemos con certeza quién es, así que
  // podemos preguntarle al backend si la cuenta tiene llaves de acceso
  // (listPasskeys ya exige estar autenticado) sin exponer esa info a nadie
  // no autenticado. Si tiene, se marca este dispositivo como "recordado" en
  // el momento — así la próxima vez arranca directo en modo passkey aunque
  // acá nunca se haya hecho la ceremonia de WebAuthn.
  const rememberPasskeyIfEnabled = async (user) => {
    try {
      const list = functions.httpsCallable('listPasskeys');
      const { data } = await list();
      if (data?.passkeys?.length > 0) {
        rememberPasskeyForEmail(user.email);
      }
    } catch {
      // Sin passkeys, sin conexión, o lo que sea: no pasa nada, se sigue
      // pidiendo contraseña en este dispositivo hasta que se sepa con certeza.
    }
  };

  const attemptSignIn = async (email, password) => {
    setIsLoading(true);
    try {
      const loginResponse = await signInUser(email, password);
      isLoggedIn(loginResponse.user);
      navigate('/home');
      rememberPasskeyIfEnabled(loginResponse.user);
      saveAccessLog(
        loginResponse.user,
        position?.latitude || null,
        position?.longitude || null
      );
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(getAuth(), error));
      } else {
        console.error(error.message);
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    await attemptSignIn(formData.email, formData.password);
  };

  // Arma el autocompletado de passkeys del navegador (WebAuthn Conditional
  // UI): queda "escuchando" en silencio hasta que el usuario toca la
  // sugerencia y confirma con huella/rostro, sin bloquear el resto de la
  // pantalla ni mostrar ningún prompt propio. Solo en navegador — la app
  // Android empaquetada no tiene este mecanismo, ahí se usa el botón manual.
  useEffect(() => {
    if (isNativePasskeyBridgeAvailable()) return;
    let cancelled = false;

    (async () => {
      const supported = await browserSupportsWebAuthnAutofill();
      if (cancelled || !supported) return;

      try {
        const getOptions = functions.httpsCallable('generatePasskeyLoginOptions');
        const { data } = await getOptions({});
        const { options: optionsJSON, challengeKey } = data;
        const authResponse = await startAuthentication({ optionsJSON, useBrowserAutofill: true });
        if (cancelled) return;

        const verify = functions.httpsCallable('verifyPasskeyLogin');
        const { data: verifyData } = await verify({ challengeKey, response: authResponse });
        const loginResponse = await auth.signInWithCustomToken(verifyData.token);
        rememberPasskeyForEmail(loginResponse.user.email);
        isLoggedIn(loginResponse.user);
        navigate('/home');
        saveAccessLog(
          loginResponse.user,
          position?.latitude || null,
          position?.longitude || null
        );
      } catch (err) {
        // El usuario nunca tocó la sugerencia, no hay llaves para autocompletar,
        // o navegó a otra pantalla — no hay nada que mostrarle acá.
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login sin contraseña, "usernameless": no hace falta el email. El backend
  // manda opciones genéricas para el sitio (sin allowCredentials), el
  // selector de passkeys del dispositivo muestra las que ya tenga guardadas,
  // y quién sea se identifica después por el userHandle que trae la propia
  // respuesta firmada (ver verifyPasskeyLogin). Si el usuario sí escribió el
  // email, se lo mandamos igual para acotar el selector a esa cuenta.
  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      const getOptions = functions.httpsCallable('generatePasskeyLoginOptions');
      const { data } = await getOptions({ email: formData.email || undefined });
      const { options: optionsJSON, challengeKey } = data;
      const authResponse = isNativePasskeyBridgeAvailable()
        ? await nativeStartPasskeyLogin(optionsJSON)
        : await startAuthentication({ optionsJSON });

      const verify = functions.httpsCallable('verifyPasskeyLogin');
      const { data: verifyData } = await verify({
        challengeKey,
        response: authResponse,
        email: formData.email || undefined,
      });

      const loginResponse = await auth.signInWithCustomToken(verifyData.token);
      rememberPasskeyForEmail(loginResponse.user.email);
      isLoggedIn(loginResponse.user);
      navigate('/home');
      saveAccessLog(
        loginResponse.user,
        position?.latitude || null,
        position?.longitude || null
      );
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError(<T>Inicio de sesión con llave de acceso cancelado.</T>);
      } else {
        setError(err.message || 'No se pudo iniciar sesión con la llave de acceso.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // En navegador abre el popup de Google; en la app Android usa el
  // Credential Manager nativo (ver signInWithGoogle en firebase.js). Si a la
  // cuenta le faltan datos (usuario, teléfono, localidad) va al formulario
  // de registro sin email ni contraseña, y de ahí al inicio.
  const handleGoogleLogin = async () => {
    if (pendingGoogleSignIn) return;
    setError('');
    setGoogleLoading(true);
    const pending = signInWithGoogle();
    pendingGoogleSignIn = pending;
    try {
      const { user, isNewUser, needsProfile } = await pending;
      isLoggedIn(user);
      if (needsProfile) {
        navigate('/register', { state: { google: true } });
      } else {
        navigate('/home');
      }
      if (!isNewUser) rememberPasskeyIfEnabled(user);
      saveAccessLog(
        user,
        position?.latitude || null,
        position?.longitude || null
      );
    } catch (err) {
      handleGoogleError(err);
    } finally {
      pendingGoogleSignIn = null;
      setGoogleLoading(false);
    }
  };

  const handleMfaSuccess = async (result) => {
    isLoggedIn(result.user);
    navigate('/home');
    rememberPasskeyIfEnabled(result.user);
    saveAccessLog(
      result.user,
      position?.latitude || null,
      position?.longitude || null
    );
  };

  // El 2FA nativo de Firebase ya quedó desactivado en el servidor (useMfaBackupCode);
  // reintentar el login normal ahora debería completar sin pedir MFA.
  const handleMfaRecovered = async () => {
    setMfaResolver(null);
    await attemptSignIn(formData.email, formData.password);
  };

  if (mfaResolver) {
    return (
      <AuthLayout>
        <MfaChallenge
          resolver={mfaResolver}
          email={formData.email}
          password={formData.password}
          onSuccess={handleMfaSuccess}
          onRecovered={handleMfaRecovered}
          onCancel={() => setMfaResolver(null)}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {showPresentation ? (
        <WelcomeSlides onFinish={handleFinishSlides} />
      ) : (
        <>
          {geoLoading && <div style={{textAlign:'center',color:'#aaa'}}><T>Obteniendo ubicación...</T></div>}
          {/* Ciudad eliminada, solo se usan coordenadas */}
          <LoginForm
            formData={formData}
            error={error}
            isLoading={isLoading}
            showPassword={showPassword}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            setShowPassword={setShowPassword}
            handlePasskeyLogin={handlePasskeyLogin}
            passkeyLoading={passkeyLoading}
            handleGoogleLogin={handleGoogleLogin}
            googleLoading={googleLoading}
            passkeyAvailable={
              (browserSupportsWebAuthn() || isNativePasskeyBridgeAvailable()) && deviceHasAnyPasskeyRemembered()
            }
            passkeyMode={
              (browserSupportsWebAuthn() || isNativePasskeyBridgeAvailable()) && deviceHasAnyPasskeyRemembered() && !usePasswordInstead
            }
            onTogglePasskeyMode={() => {
              setError('');
              setUsePasswordInstead((v) => !v);
            }}
          />
        </>
      )}
    </AuthLayout>
  );
};

export default Login;
